// [S-210] Template Management — authoring console for L1 + L2 templates.
//
// Per Velocity Plan epic E-TEMPLATE-MGMT: replaces the NotYetBuilt
// placeholder at /template-management. Distinct from /library which is
// the read-only certified browser.
//
// Surfaces every lifecycle state (draft, certified, under_review,
// re_certified, deprecated) for tenant_admin (L2 + L1) and
// platform_admin (L1 + their tenant's L2 if any). Filters: lifecycle,
// layer, deal type, guild, talent tier. Search by id + content
// substring (client-side — list is bounded by useAdminTemplates).
//
// Bulk select for multi-flag-for-review and multi-deprecate (the eligible
// certified/re_certified subset; bulk records no successor — keeps records
// so contracts built from a template stay intact). Single-item Deprecate
// uses the existing DeprecateTemplateModal (needs successor picker). Per-row
// actions: Open, Edit, Flag, Deprecate. New template + Bulk upload CTAs
// in the page header → /library/new (S-211 will extend that flow).
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { TALENT_TIERS } from "../lib/talentTier";
import {
  useAdminTemplates,
  useDeprecate,
  useExcludeTemplate,
  useExportTemplate,
  useFlagForReview,
  useIncludeTemplate,
  useTemplateExclusions,
  type CertifiedLibraryFilter,
  type CertifiedTemplateRow,
} from "../data/templatesQuery";
import {
  BulkDeprecateModal,
  BulkExcludeModal,
  BulkFlagModal,
  DeprecateTemplateModal,
  FlagForReviewModal,
} from "../components/templates/TemplateLifecycleModals";
import {
  Alert,
  Button,
  Checkbox,
  PageHead,
  Pagination,
  Select,
  usePagination,
} from "../components";
import { TemplateRowMenu } from "../components/templates/TemplateRowMenu";
import { EditTagsModal } from "../components/templates/EditTagsModal";
import { useActorLabels } from "../data/identitiesQuery";
import { sessionCan } from "../auth/capabilities";

type LifecycleState = CertifiedTemplateRow["lifecycleState"];
type LayerSource = CertifiedTemplateRow["layerSource"];

// [S-211] Column sorting. Every data column is sortable asc/desc; the header
// toggles direction (and resets to a sensible default when switching column).
type SortKey = "name" | "state" | "layer" | "deal" | "owner" | "added";
type SortState = { key: SortKey; dir: "asc" | "desc" };

// Logical lifecycle order for the State sort (draft → deprecated), not the
// alphabetical order a raw string compare would give.
const STATE_RANK: Record<LifecycleState, number> = {
  draft: 0,
  under_review: 1,
  certified: 2,
  re_certified: 3,
  deprecated: 4,
};

function sortValue(t: CertifiedTemplateRow, key: SortKey): string | number {
  switch (key) {
    case "name":
      return templateDisplayName(t).toLowerCase();
    case "state":
      return STATE_RANK[t.lifecycleState];
    case "layer":
      return t.layerSource;
    case "deal":
      return `${t.dealType} ${t.guild} ${t.tier}`;
    case "owner":
      return t.submittedBy.toLowerCase();
    case "added":
      return t.effectiveFrom; // ISO string → lexical order == chronological
  }
}

function compareRows(
  a: CertifiedTemplateRow,
  b: CertifiedTemplateRow,
  sort: SortState
): number {
  const av = sortValue(a, sort.key);
  const bv = sortValue(b, sort.key);
  let cmp =
    typeof av === "number" && typeof bv === "number"
      ? av - bv
      : String(av).localeCompare(String(bv));
  if (cmp === 0) cmp = a.id.localeCompare(b.id); // stable tiebreaker
  return sort.dir === "asc" ? cmp : -cmp;
}

const LIFECYCLE_OPTIONS: ReadonlyArray<{ value: LifecycleState; label: string }> = [
  { value: "draft", label: "Draft" },
  { value: "certified", label: "Certified" },
  { value: "under_review", label: "Under review" },
  { value: "re_certified", label: "Re-certified" },
  // [S-672] Displayed as "Retired" (Bill: "deprecated" reads as ambiguous
  // jargon) — the underlying lifecycleState value stays "deprecated".
  { value: "deprecated", label: "Retired" },
];

const LAYER_OPTIONS: ReadonlyArray<{ value: LayerSource; label: string }> = [
  { value: "L1", label: "L1 — Templates" },
  { value: "L2", label: "L2 — Studio template" },
];

const DEAL_TYPES: ReadonlyArray<{ value: CertifiedTemplateRow["dealType"]; label: string }> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{ value: CertifiedTemplateRow["guild"]; label: string }> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS: ReadonlyArray<{ value: CertifiedTemplateRow["tier"]; label: string }> = [
  ...TALENT_TIERS,
];

type LocalFilters = CertifiedLibraryFilter & {
  lifecycle?: LifecycleState;
  layer?: LayerSource;
};

export function TemplateManagement(): JSX.Element {
  const navigate = useNavigate();
  const session = useSession();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";

  const [filters, setFilters] = useState<LocalFilters>({});
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [flagging, setFlagging] = useState<CertifiedTemplateRow | null>(null);
  const [deprecating, setDeprecating] = useState<CertifiedTemplateRow | null>(null);
  // [S-216] Retag any template from the list — works in every lifecycle state.
  const [retagging, setRetagging] = useState<CertifiedTemplateRow | null>(null);
  const [bulkFlagging, setBulkFlagging] = useState(false);
  const [bulkDeprecating, setBulkDeprecating] = useState(false);
  const [bulkExcluding, setBulkExcluding] = useState(false);
  // Newest-first by default so a just-imported template lands at the top.
  const [sort, setSort] = useState<SortState>({ key: "added", dir: "desc" });

  // Server-side filters: dealType, guild, tier (handled by useAdminTemplates).
  // Client-side filters: lifecycle, layer, search — bounded N keeps this cheap.
  const serverFilter: CertifiedLibraryFilter = {
    dealType: filters.dealType,
    guild: filters.guild,
    tier: filters.tier,
  };

  const query = useAdminTemplates(tenantId, serverFilter);

  // [S-718] The platform's L1 templates a tenant has opted out of — S-063b's
  // backend, previously with no web UI. Only meaningful with a tenant scope
  // (platform_admin browsing L1-only, tenantId === "", never fetches).
  const exclusionsQuery = useTemplateExclusions(tenantId);
  const excludedIds = useMemo(
    () => new Set((exclusionsQuery.data ?? []).map((x) => x.templateId)),
    [exclusionsQuery.data]
  );
  const excludeTemplate = useExcludeTemplate();
  const includeTemplate = useIncludeTemplate();

  const rows = useMemo<CertifiedTemplateRow[]>(() => {
    const all = query.data ?? [];
    const needle = search.trim().toLowerCase();
    const filtered = all.filter((t) => {
      if (filters.lifecycle !== undefined && t.lifecycleState !== filters.lifecycle) {
        return false;
      }
      if (filters.layer !== undefined && t.layerSource !== filters.layer) {
        return false;
      }
      if (needle.length === 0) return true;
      return (
        // Search by the name a reviewer knows (title/filename) first, then id,
        // owner, and content.
        (t.title ?? "").toLowerCase().includes(needle) ||
        t.id.toLowerCase().includes(needle) ||
        t.submittedBy.toLowerCase().includes(needle) ||
        t.content.toLowerCase().includes(needle)
      );
    });
    return filtered.sort((a, b) => compareRows(a, b, sort));
  }, [query.data, filters.lifecycle, filters.layer, search, sort]);

  // Pages the (filtered) rows for display. Selection + bulk actions deliberately
  // operate on the whole filtered set, not just the visible page.
  const pager = usePagination(rows, 24);

  // Owner column shows a name, never the raw submittedBy uid — resolve the
  // visible page's uids in one batched call.
  const ownerUids = useMemo(
    () => pager.pageItems.map((t) => t.submittedBy),
    [pager.pageItems]
  );
  const ownerLabels = useActorLabels(ownerUids, tenantId);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected]
  );

  // Only certified / re_certified templates can be deprecated. Bulk deprecate
  // acts on that subset and reports the rest as skipped (no successor in bulk —
  // per-row Deprecate owns successor mapping).
  const deprecatableSelected = useMemo(
    () =>
      selectedRows.filter(
        (r) =>
          r.lifecycleState === "certified" ||
          r.lifecycleState === "re_certified"
      ),
    [selectedRows]
  );

  // [S-718] Bulk exclude — same gate as the per-row action (own-tenant scope
  // + exclusion:manage), applied to whichever selected rows are actually
  // eligible: L1 only, not already excluded. exclude() is idempotent
  // per-template, but reporting an accurate "will change" count matches the
  // deprecate bulk-action UX rather than silently no-op-ing on the rest.
  const canBulkExclude =
    tenantId.length > 0 && sessionCan(session, "exclusion:manage");
  const excludableSelected = useMemo(
    () =>
      selectedRows.filter(
        (r) => r.layerSource === "L1" && !excludedIds.has(r.id)
      ),
    [selectedRows, excludedIds]
  );

  const allVisibleSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.id));

  const flagBulk = useFlagForReview();
  const deprecateBulk = useDeprecate();
  const exportTemplate = useExportTemplate();

  const handleToggleRow = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAllVisible = (): void => {
    setSelected((prev) => {
      if (rows.every((r) => prev.has(r.id))) {
        // All visible were selected → unselect just those (keep any
        // selection on rows outside the current filter intact).
        const next = new Set(prev);
        for (const r of rows) next.delete(r.id);
        return next;
      }
      const next = new Set(prev);
      for (const r of rows) next.add(r.id);
      return next;
    });
  };

  // Reason captured by <BulkFlagModal>; the loop stays here because it owns
  // the selection. Sequential to keep audit ordering deterministic and the
  // error surface scoped (one failure shouldn't silently leave the rest in
  // limbo). Could batch when the server gains a batch endpoint.
  const runBulkFlag = async (reason: string): Promise<void> => {
    for (const row of selectedRows) {
      try {
        await flagBulk.mutateAsync({
          templateId: row.id,
          tenantId: row.tenantId,
          reason,
        });
      } catch {
        // Continue — user sees per-call errors via the modal error line.
      }
    }
    setSelected(new Set());
    setBulkFlagging(false);
  };

  // Bulk deprecate the eligible (certified / re_certified) subset. Sequential
  // for deterministic audit ordering + scoped errors, mirroring runBulkFlag.
  // asOf = now (must be after each template's effectiveFrom); successor is null
  // in bulk — deprecation keeps the record so contracts built from it stay
  // intact, so a successor isn't required to retire it safely.
  const runBulkDeprecate = async (): Promise<void> => {
    const asOf = new Date().toISOString();
    for (const row of deprecatableSelected) {
      try {
        await deprecateBulk.mutateAsync({
          templateId: row.id,
          tenantId: row.tenantId,
          asOf,
          successorId: null,
        });
      } catch {
        // Continue — the modal surfaces per-call errors.
      }
    }
    setSelected(new Set());
    setBulkDeprecating(false);
  };

  // Bulk exclude the eligible (L1, not-already-excluded) subset from this
  // tenant. Sequential for deterministic audit ordering + scoped errors,
  // mirroring runBulkFlag/runBulkDeprecate.
  const runBulkExclude = async (reason: string | null): Promise<void> => {
    for (const row of excludableSelected) {
      try {
        await excludeTemplate.mutateAsync({
          tenantId,
          templateId: row.id,
          reason,
        });
      } catch {
        // Continue — the modal surfaces per-call errors.
      }
    }
    setSelected(new Set());
    setBulkExcluding(false);
  };

  const counts = useMemo(() => {
    const all = query.data ?? [];
    return {
      total: all.length,
      shown: rows.length,
      draft: all.filter((t) => t.lifecycleState === "draft").length,
      under_review: all.filter((t) => t.lifecycleState === "under_review").length,
      deprecated: all.filter((t) => t.lifecycleState === "deprecated").length,
    };
  }, [query.data, rows.length]);

  const layerLabel =
    tenantId.length > 0 ? "L1 + your organization L2" : "L1 (no tenant scope)";

  // Click a header to sort by it; click again to flip direction. Switching to a
  // new column resets to a sensible default (dates newest-first, text A→Z).
  const toggleSort = (key: SortKey): void => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "added" ? "desc" : "asc" }
    );
  };
  // Render a sortable column header. A plain function (not a component) to keep
  // it out of the nested-component lint while closing over sort/toggleSort.
  const sortTh = (key: SortKey, label: string, cellClass = ""): JSX.Element => {
    const active = sort.key === key;
    return (
      <div
        role="columnheader"
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className={`tplmgmt__cell ${cellClass}`.trim()}
      >
        <Button
          type="button"
          variant="bare"
          className={active ? "tplmgmt__sort is-active" : "tplmgmt__sort"}
          onClick={() => toggleSort(key)}
          data-testid={`tplmgmt-sort-${key}`}
        >
          {label}
          <span className="tplmgmt__sort__ind" aria-hidden="true">
            {active ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕"}
          </span>
        </Button>
      </div>
    );
  };

  return (
    <>
      <PageHead
        eyebrow="Platform · template management"
        title={
          <>
            Manage <em>every</em> template state.
          </>
        }
        sub={
          <>
            Draft, certified, under review, re-certified, retired — across {layerLabel}.{" "}
            {query.isLoading
              ? "Loading…"
              : `${counts.shown.toLocaleString()} of ${counts.total.toLocaleString()} match your filters.`}
          </>
        }
        cta={
          <>
            <Link to="/library/new" className="btn btn--p btn--s" data-testid="tplmgmt-new">
              + New template
            </Link>
            <Link
              to="/template-management/bulk"
              className="btn btn--s"
              data-testid="tplmgmt-bulk"
            >
              Import templates
            </Link>
            <Link to="/library" className="btn btn--g btn--s">
              View certified templates
            </Link>
          </>
        }
      />

      <div className="tplmgmt__filters">
        <span className="tplmgmt__filterlbl">Filter</span>
        <FilterSelect
          label="Lifecycle"
          value={filters.lifecycle}
          options={LIFECYCLE_OPTIONS}
          onChange={(v) => setFilters((f) => ({ ...f, lifecycle: v }))}
        />
        <FilterSelect
          label="Layer"
          value={filters.layer}
          options={LAYER_OPTIONS}
          onChange={(v) => setFilters((f) => ({ ...f, layer: v }))}
        />
        <FilterSelect
          label="Deal type"
          value={filters.dealType}
          options={DEAL_TYPES}
          onChange={(v) => setFilters((f) => ({ ...f, dealType: v }))}
        />
        <FilterSelect
          label="Guild"
          value={filters.guild}
          options={GUILDS}
          onChange={(v) => setFilters((f) => ({ ...f, guild: v }))}
        />
        <FilterSelect
          label="Talent tier"
          value={filters.tier}
          options={TIERS}
          onChange={(v) => setFilters((f) => ({ ...f, tier: v }))}
        />

        <input
          type="search"
          placeholder="Search name, owner, or content…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="tplmgmt__search"
          aria-label="Search templates"
          data-testid="tplmgmt-search"
        />

        <span className="tplmgmt__counts" aria-live="polite">
          <span>{counts.draft} drafts</span>
          <span>·</span>
          <span>{counts.under_review} under review</span>
          <span>·</span>
          <span>{counts.deprecated} retired</span>
        </span>
      </div>

      {selectedRows.length > 0 && (
        <div className="tplmgmt__bulk" role="region" aria-label="Bulk actions">
          <span className="tplmgmt__bulk__count">
            <b>{selectedRows.length}</b> selected
          </span>
          <Button
            variant="secondary"
            className="btn--s"
            onClick={() => setBulkFlagging(true)}
            disabled={flagBulk.isPending}
            data-testid="tplmgmt-bulk-flag"
          >
            {flagBulk.isPending ? "Flagging…" : "Flag for review"}
          </Button>
          <Button
            variant="danger"
            size="s"
            onClick={() => setBulkDeprecating(true)}
            disabled={deprecateBulk.isPending || deprecatableSelected.length === 0}
            title={
              deprecatableSelected.length === 0
                ? "Only certified or re-certified templates can be deprecated"
                : undefined
            }
            data-testid="tplmgmt-bulk-deprecate"
          >
            {deprecateBulk.isPending
              ? "Deprecating…"
              : `Deprecate${
                  deprecatableSelected.length > 0
                    ? ` (${deprecatableSelected.length})`
                    : ""
                }`}
          </Button>
          {canBulkExclude && (
            <Button
              variant="secondary"
              className="btn--s"
              onClick={() => setBulkExcluding(true)}
              disabled={excludeTemplate.isPending || excludableSelected.length === 0}
              title={
                excludableSelected.length === 0
                  ? "Only L1 templates not already excluded can be excluded"
                  : undefined
              }
              data-testid="tplmgmt-bulk-exclude"
            >
              {excludeTemplate.isPending
                ? "Excluding…"
                : `Exclude${
                    excludableSelected.length > 0
                      ? ` (${excludableSelected.length})`
                      : ""
                  }`}
            </Button>
          )}
          <Button
            variant="ghost"
            className="btn--s"
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}

      {(query.isLoading || (query.isFetching && rows.length === 0)) && (
        <div className="tplmgmt__state" role="status" aria-live="polite">
          Loading templates…
        </div>
      )}

      {query.isError && (
        <Alert label="Couldn't load templates" tone="error">
          {query.error?.message ?? "Unknown error"}
        </Alert>
      )}

      {!query.isLoading && !query.isFetching && !query.isError && rows.length === 0 && (
        <div className="tplmgmt__state" data-testid="tplmgmt-empty">
          <div className="tplmgmt__state__title">No templates match</div>
          Loosen the filters above or clear the search.
        </div>
      )}

      {!query.isLoading && !query.isError && rows.length > 0 && (
        <div className="tplmgmt__tbl" role="table" aria-label="Templates">
          <div className="tplmgmt__row tplmgmt__row--head" role="row">
            <div role="columnheader" className="tplmgmt__cell tplmgmt__cell--check">
              <Checkbox
                aria-label={allVisibleSelected ? "Unselect all visible" : "Select all visible"}
                checked={allVisibleSelected}
                onCheckedChange={() => handleToggleAllVisible()}
                data-testid="tplmgmt-select-all"
              />
            </div>
            {sortTh("name", "Template", "tplmgmt__cell--id")}
            {sortTh("state", "State")}
            {sortTh("layer", "Layer")}
            {sortTh("deal", "Deal · Guild · Tier")}
            {sortTh("owner", "Owner")}
            {sortTh("added", "Added")}
            <div role="columnheader" className="tplmgmt__cell tplmgmt__cell--act">Actions</div>
          </div>
          {pager.pageItems.map((t) => (
            <TemplateRow
              key={t.id}
              template={t}
              ownerLabel={ownerLabels?.get(t.submittedBy)}
              selected={selected.has(t.id)}
              tenantId={tenantId}
              isExcluded={excludedIds.has(t.id)}
              onToggle={() => handleToggleRow(t.id)}
              onOpen={() => navigate(openHref(t))}
              onEdit={() => navigate(`/library/${encodeURIComponent(t.id)}/edit`)}
              onEditTags={() => setRetagging(t)}
              onFlag={() => setFlagging(t)}
              onDeprecate={() => setDeprecating(t)}
              onExclude={() => excludeTemplate.mutate({ tenantId, templateId: t.id })}
              onInclude={() => includeTemplate.mutate({ tenantId, templateId: t.id })}
              onExport={(template) =>
                exportTemplate.mutate({
                  templateId: template.id,
                  tenantId: template.tenantId,
                })
              }
              exporting={exportTemplate.isPending && exportTemplate.variables?.templateId === t.id}
            />
          ))}
        </div>
      )}
      {!query.isLoading && !query.isError && rows.length > 0 && (
        <Pagination
          page={pager.page}
          pageCount={pager.pageCount}
          onPageChange={pager.setPage}
          total={pager.total}
          rangeStart={pager.rangeStart}
          rangeEnd={pager.rangeEnd}
          itemLabel="template"
        />
      )}

      <EditTagsModal template={retagging} onClose={() => setRetagging(null)} />
      <FlagForReviewModal
        template={flagging}
        onClose={() => setFlagging(null)}
      />
      <BulkFlagModal
        open={bulkFlagging}
        count={selectedRows.length}
        busy={flagBulk.isPending}
        error={
          flagBulk.isError
            ? (flagBulk.error?.message ?? "Failed to flag one or more templates")
            : null
        }
        onConfirm={(reason) => void runBulkFlag(reason)}
        onClose={() => setBulkFlagging(false)}
      />
      <BulkDeprecateModal
        open={bulkDeprecating}
        count={deprecatableSelected.length}
        skipped={selectedRows.length - deprecatableSelected.length}
        busy={deprecateBulk.isPending}
        error={
          deprecateBulk.isError
            ? (deprecateBulk.error?.message ??
              "Failed to deprecate one or more templates")
            : null
        }
        onConfirm={() => void runBulkDeprecate()}
        onClose={() => setBulkDeprecating(false)}
      />
      <BulkExcludeModal
        open={bulkExcluding}
        count={excludableSelected.length}
        skipped={selectedRows.length - excludableSelected.length}
        busy={excludeTemplate.isPending}
        error={
          excludeTemplate.isError
            ? (excludeTemplate.error?.message ??
              "Failed to exclude one or more templates")
            : null
        }
        onConfirm={(reason) => void runBulkExclude(reason)}
        onClose={() => setBulkExcluding(false)}
      />
      <DeprecateTemplateModal
        template={deprecating}
        siblings={query.data ?? []}
        onClose={() => setDeprecating(null)}
      />
    </>
  );
}

type FilterSelectProps<V extends string> = {
  label: string;
  value: V | undefined;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (value: V | undefined) => void;
};

function FilterSelect<V extends string>({
  label,
  value,
  options,
  onChange,
}: FilterSelectProps<V>): JSX.Element {
  const isOn = value !== undefined;
  return (
    <span className={isOn ? "tplmgmt__filter is-on" : "tplmgmt__filter"}>
      <Select
        label={label}
        value={value}
        options={options}
        onValueChange={onChange}
        triggerClassName="tplmgmt__filter__select"
      />
      {isOn && (
        <Button
          variant="bare"
          className="tplmgmt__filter__clear"
          aria-label={`Clear ${label}`}
          onClick={() => onChange(undefined)}
        >
          ×
        </Button>
      )}
    </span>
  );
}

type TemplateRowProps = {
  template: CertifiedTemplateRow;
  ownerLabel: string | undefined;
  selected: boolean;
  /** [S-718] The caller's own tenant scope ("" when platform_admin is
   *  browsing L1-only) — exclusion only ever applies with a real tenant. */
  tenantId: string;
  isExcluded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onEdit: () => void;
  onEditTags: () => void;
  onFlag: () => void;
  onDeprecate: () => void;
  onExclude: () => void;
  onInclude: () => void;
  onExport: (template: CertifiedTemplateRow) => void;
  exporting: boolean;
};

function TemplateRow({
  template: t,
  ownerLabel,
  selected,
  tenantId,
  isExcluded,
  onToggle,
  onOpen,
  onEdit,
  onEditTags,
  onFlag,
  onDeprecate,
  onExclude,
  onInclude,
  onExport,
  exporting,
}: TemplateRowProps): JSX.Element {
  const session = useSession();
  const canEdit = t.lifecycleState === "draft";
  const canFlag =
    t.lifecycleState === "certified" || t.lifecycleState === "re_certified";
  const canDeprecate =
    t.lifecycleState === "certified" || t.lifecycleState === "re_certified";
  const canExport = t.lifecycleState !== "draft";
  // [S-216] Retag is allowed in ANY lifecycle state (tags are metadata) — this
  // is the list-level entry point so drafts / deprecated / under-review rows,
  // which never reach the certified-only detail page, can still be tagged. Gate
  // on template:tag for the row's layer (mirrors TemplateDetail), so a persona
  // without the capability doesn't see a button that would 403.
  const isL1 = t.layerSource === "L1";
  const l2TenantMatch =
    !isL1 &&
    t.tenantId !== null &&
    session?.studioId !== null &&
    session?.studioId !== undefined &&
    `tenant-${session.studioId}` === t.tenantId;
  const canTag = isL1
    ? sessionCan(session, "template:tag", { layer: "L1" })
    : l2TenantMatch && sessionCan(session, "template:tag", { layer: "L2" });
  // [S-718] Exclusion (S-063b) is an L1-only, per-tenant opt-out — only shows
  // with a real tenant scope in view (never for platform_admin's L1-only
  // browse, tenantId === "") and only for the caller's own tenant, so this
  // reuses the same non-layer-scoped sessionCan check the service's
  // requireCapability(cap, {tenantId: OWN tenant}) makes on the backend.
  const canExclude = isL1 && tenantId.length > 0 && sessionCan(session, "exclusion:manage");
  // Actions that live in the overflow menu. Edit is inline (draft-only), so
  // it's excluded here — a draft with no other action renders Open + Edit only.
  const hasMenu = canTag || canFlag || canDeprecate || canExport || canExclude;

  return (
    <div
      className={selected ? "tplmgmt__row is-selected" : "tplmgmt__row"}
      role="row"
      data-testid={`tplmgmt-row-${t.id}`}
    >
      <div role="cell" className="tplmgmt__cell tplmgmt__cell--check">
        <Checkbox
          aria-label={`Select ${t.id}`}
          checked={selected}
          onCheckedChange={() => onToggle()}
          data-testid={`tplmgmt-select-${t.id}`}
        />
      </div>
      <div role="cell" className="tplmgmt__cell tplmgmt__cell--id">
        {/* Lead with the human name (uploaded title / filename), not the opaque
            id — a reviewer selects by what they uploaded. The id stays on hover
            for support. Falls back to the deal/guild/tier label for legacy rows
            with no title. */}
        <Button
          variant="bare"
          className="tplmgmt__id"
          onClick={onOpen}
          title={t.id}
          data-testid={`tplmgmt-open-${t.id}`}
        >
          {templateDisplayName(t)}
        </Button>
      </div>
      <div role="cell" className="tplmgmt__cell">
        <StateBadge state={t.lifecycleState} />
        {/* [S-721 follow-up] A draft forked from a Marketplace import carries
            real certified provenance even though ITS OWN state is "draft" —
            distinguishes "never certified by anyone" from "was certified by
            the platform, now being customized before re-publishing." Scoped
            to draft only: once re-certified it's fully the tenant's own
            certified template again, the origin note stops being load-bearing. */}
        {t.importedFrom != null && t.lifecycleState === "draft" && (
          <span
            className="tplmgmt__provenance-badge"
            title="Forked from a certified platform template — re-certify to publish your changes for use"
            data-testid={`tplmgmt-provenance-${t.id}`}
          >
            Forked from certified
          </span>
        )}
      </div>
      <div role="cell" className="tplmgmt__cell">
        <span className={t.layerSource === "L2" ? "tplmgmt__layer tplmgmt__layer--l2" : "tplmgmt__layer tplmgmt__layer--l1"}>
          {t.layerSource}
        </span>
        {isExcluded && (
          <span
            className="tplmgmt__excluded-badge"
            title="Hidden from this tenant's drafting agent and browse screens"
            data-testid={`tplmgmt-excluded-${t.id}`}
          >
            Excluded
          </span>
        )}
      </div>
      <div role="cell" className="tplmgmt__cell">
        <span className="tplmgmt__meta">
          {prettyDealType(t.dealType)} · {prettyGuild(t.guild)} · {prettyTier(t.tier)}
        </span>
      </div>
      <div role="cell" className="tplmgmt__cell tplmgmt__cell--owner">
        {ownerLabel ?? "—"}
      </div>
      <div role="cell" className="tplmgmt__cell">
        {formatDate(t.effectiveFrom)}
      </div>
      <div role="cell" className="tplmgmt__cell tplmgmt__cell--act">
        {/* One emphasized inline action (Open); the rest collapse into a ⋯
            overflow menu so a row never crowds up to five equal buttons.
            A draft's only extra action is Edit, so it stays inline and no
            menu is rendered. */}
        <Button
          variant="secondary"
          className="btn--xs"
          onClick={onOpen}
          data-testid={`tplmgmt-openbtn-${t.id}`}
        >
          Open
        </Button>
        {canEdit && (
          <Button
            variant="ghost"
            className="btn--xs"
            onClick={onEdit}
            data-testid={`tplmgmt-edit-${t.id}`}
          >
            Edit
          </Button>
        )}
        {hasMenu && (
          <TemplateRowMenu
            templateId={t.id}
            canTag={canTag}
            canFlag={canFlag}
            canExport={canExport}
            canDeprecate={canDeprecate}
            canExclude={canExclude}
            isExcluded={isExcluded}
            exporting={exporting}
            onEditTags={onEditTags}
            onFlag={onFlag}
            onExport={() => onExport(t)}
            onDeprecate={onDeprecate}
            onExclude={onExclude}
            onInclude={onInclude}
          />
        )}
      </div>
    </div>
  );
}

// [S-640] Hover hint per state so a reviewer understands what each means — in
// particular why a retired template is read-only and how to reuse it (Bill
// couldn't tell). The detail page (useTemplateDetail) only renders certified /
// re_certified; deprecated/draft rows open in the editor route.
// [S-672] Label reads "Retired" (Bill: "deprecated" is ambiguous jargon) —
// the underlying lifecycleState value stays "deprecated".
const STATE_HINT: Record<LifecycleState, string> = {
  draft: "Draft — not yet certified, so not usable for review or drafting.",
  certified: "Certified — attorney-approved and in active use.",
  under_review: "Under review — flagged for re-approval against a new CBA/regulation.",
  re_certified: "Re-certified — re-approved after review.",
  deprecated:
    "Retired — superseded and read-only. To reuse it, open a certified template and use 'Edit as new draft'.",
};

function StateBadge({ state }: { state: LifecycleState }): JSX.Element {
  const cls = `tplmgmt__state-pill tplmgmt__state-pill--${state.replace("_", "")}`;
  return (
    <span className={cls} title={STATE_HINT[state]} data-testid={`tplmgmt-state-${state}`}>
      {prettyState(state)}
    </span>
  );
}

function prettyState(s: LifecycleState): string {
  switch (s) {
    case "draft":
      return "Draft";
    case "certified":
      return "Certified";
    case "under_review":
      return "Under review";
    case "re_certified":
      return "Re-certified";
    case "deprecated":
      return "Retired";
  }
}

// Where "Open" (and the row's id) navigates. Only certified /
// re_certified templates exist in the certified-library detail
// (/library/:id → TemplateDetail rejects every other state via
// useTemplateDetail). Drafts, under_review, and deprecated rows route
// to /library/:id/edit instead — useAdminTemplate there reads any
// state and renders either the draft editor or a read-only banner,
// so the link never lands on a "couldn't load template" error.
function openHref(t: CertifiedTemplateRow): string {
  const id = encodeURIComponent(t.id);
  return t.lifecycleState === "certified" || t.lifecycleState === "re_certified"
    ? `/library/${id}`
    : `/library/${id}/edit`;
}

function prettyDealType(d: CertifiedTemplateRow["dealType"]): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function prettyGuild(g: CertifiedTemplateRow["guild"]): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyTier(t: CertifiedTemplateRow["tier"]): string {
  return t.replace(/^\w/, (c) => c.toUpperCase());
}

// The name a reviewer recognizes: the uploaded title/filename when set, else a
// readable deal/guild/tier label (legacy/seeded rows have no title). Never the
// opaque id — that moves to a hover tooltip.
function templateDisplayName(t: CertifiedTemplateRow): string {
  const title = t.title?.trim();
  if (title !== undefined && title.length > 0) return title;
  return `${prettyGuild(t.guild)} · ${prettyDealType(t.dealType)} · ${prettyTier(t.tier)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
